import { getPageFromHash, getPagesCount } from "@client/content/controls/Pagination/lib";
import { type GridItem } from "@client/controls/Grid/component";
import FluidGrid from "@client/controls/Grid/Fluid/component";
import LoadingContainer from "@client/controls/Loading/component";
import { scrollToElement } from "@client/lib/misc";
import { type ID } from "@client/types";
import classnames from "classnames";
import { isUndefined } from "lodash";
import * as React from "react";
import ContentPagination, { ContentPreviousNextPagination } from "../Pagination/component";
import GridListGrid from "./Grid/component";
import "./style.less";

export enum GridListLayout {
  Grid = "Grid",
  Masonry = "Masonry"
}

export interface GridListComponentMapProps<TItemProps extends {} = {}, TItem extends GridItem = GridItem> {
  className?: string;
  columns: number;
  layout: GridListLayout;
  gutter: number | string;
  page: number;
  isLoading: boolean;

  hasMore?: boolean;
  pageSize?: number;
  totalItems?: number;

  items: Array<TItem | ID>;
  ItemComponent: React.ComponentType<TItemProps & TItem>;
  itemProps?: Partial<TItemProps>;
}
export interface GridListComponentActionProps {
  load: (page: number) => void;
}
export type GridListComponentProps<
  TItemProps extends {} = {},
  TItem extends GridItem = GridItem
> = GridListComponentActionProps & GridListComponentMapProps<TItemProps, TItem>;

export default class GridList<TItemProps extends {} = {}, TItem extends GridItem = GridItem> extends React.Component<
  GridListComponentProps<TItemProps, TItem>
> {
  private _ref = React.createRef<HTMLDivElement>();

  public componentDidMount() {
    if (getPageFromHash()) {
      scrollToElement(this._ref.current!);
    }
  }

  public componentDidUpdate(prevProps: Readonly<GridListComponentProps<TItemProps, TItem>>): void {
    if (prevProps.page !== this.props.page) {
      scrollToElement(this._ref.current!);
    }
  }

  public render() {
    const { className, hasMore, layout, pageSize, load, page, totalItems, ...gridProps } = this.props;

    return (
      <div
        ref={this._ref}
        className={classnames(className, "wsc_grid_list", `${layout.toString().toLowerCase()}`, {
          [`columns-${gridProps.columns}`]: layout === GridListLayout.Grid,
          loading: gridProps.isLoading
        })}
      >
        {gridProps.isLoading && <LoadingContainer opacity={0.5} isAbsolute />}
        {layout === GridListLayout.Grid && <GridListGrid {...gridProps} />}
        {layout === GridListLayout.Masonry && <FluidGrid {...gridProps} />}
        {pageSize
          ? isUndefined(totalItems)
            ? (page > 1 || gridProps.items.length === pageSize) && (
                <ContentPreviousNextPagination
                  onPreviousClick={page > 1 ? () => load(page - 1) : undefined}
                  onNextClick={hasMore ? () => load(page + 1) : undefined}
                  pageNumber={page}
                  pageSize={pageSize}
                />
              )
            : totalItems > pageSize && (
                <ContentPagination onPageClick={load} page={page} total={getPagesCount(pageSize, totalItems)} />
              )
          : null}
      </div>
    );
  }
}
